import { useEffect } from 'react'
import {
	authRepository,
	cohortsRepository,
	creditsRepository,
	libraryRepository,
	mentorsRepository,
	modalsRepository,
	onboardingRepository,
	typeformRepository,
} from '../repositories'
import profileRepository from '../repositories/profileRepository'
import companyRepository from '../repositories/companyRepository'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from 'react-router-dom'
import { authCreators } from '../store/ducks/auth'
import profileService from '../services/profileService'
import sessionService from '../services/sessionService'
import { AuthLayout } from '../layouts/Auth'
import { PrivateRoute } from './PrivateRoute'
import companyService from '../services/companyService'
import { LoginPage } from '@/pages/Auth/Login'
import LocalStorage from '@/utils/helpers/LocalStorage'
import { AdminLayout } from '@layouts/Admin'
import { HomePage } from '@/pages/Admin/Home'
import { Loader } from '@/components/molecules/Loader'
import { LibraryLayout } from '@layouts/Library'
import { SuccessLayout } from '@layouts/Success'
import { CourseLayout } from '@layouts/Course'
import { coursesRoutes } from './routes/courses'
import { ProgressCheckLayout } from '@layouts/ProgressCheck'
import { mentorsRoutes } from './routes/mentors'
import { CreditsPage } from '@/pages/Admin/Credits'
import { CreditsLayout } from '@layouts/Credits'
import { SkillsPage } from '@/pages/Admin/Skills'
import { EmbedLayout } from '@layouts/Embed'
import { AmplitudeScript } from '@/tools/AmplitudeScript'

export const routes: Array<RoutesType> = [
	{
		name: 'Login',
		path: '/entrar',
		layout: 'auth',
		element: LoginPage,
		isPrivate: false,
	},
	{
		name: 'Home',
		path: '/',
		layout: 'admin',
		roles: ['student', 'white_label'],
		element: HomePage,
		isPrivate: true,
	},
	{
		name: 'Home - Cursos Completos',
		path: '/cursos-concluidos',
		layout: 'admin',
		roles: ['student', 'white_label'],
		element: HomePage,
		isPrivate: true,
	},
	{
		name: 'Créditos',
		path: '/creditos',
		roles: ['student'],
		layout: 'credits',
		element: CreditsPage,
		isPrivate: true,
	},
	{
		name: 'Map. Habilidades',
		path: '/mapeamento-habilidades',
		layout: 'embed',
		roles: ['student', 'white_label'],
		hideInSidebar: true,
		element: SkillsPage,
		isPrivate: true,
	},
	...coursesRoutes,
	...mentorsRoutes,
]

const blockedRoutes = ['/sair']

export const layouts = {
	auth: AuthLayout,
	admin: AdminLayout,
	course: CourseLayout,
	library: LibraryLayout,
	progress: ProgressCheckLayout,
	embed: EmbedLayout,
	success: SuccessLayout,
	credits: CreditsLayout,
}

function LogoutRouter() {
	const dispatch = useAppDispatch()
	const location = useLocation()
	const navigate = useNavigate()

	useEffect(() => {
		authRepository.util.resetApiState()
		profileRepository.util.resetApiState()
		companyRepository.util.resetApiState()
		onboardingRepository.util.resetApiState()
		modalsRepository.util.resetApiState()
		cohortsRepository.util.resetApiState()
		libraryRepository.util.resetApiState()
		typeformRepository.util.resetApiState()
		mentorsRepository.util.resetApiState()
		creditsRepository.util.resetApiState()

		dispatch(authCreators.logout())

		navigate('/entrar', { state: { redirect: location.pathname } })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate, location.pathname])

	return null
}

export function AppRouter() {
	const {
		auth,
		theme: { dark },
	} = useAppSelector((state) => state)

	sessionService.restoreSession()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const location = useLocation()
	const storage = new LocalStorage()

	const queryString = new URLSearchParams(location.search)

	const token = queryString.get('token')

	if (token) dispatch(authCreators.authenticate({ token }))

	const {
		isLoading: loadingProfile,
		isSuccess,
		isError,
	} = profileService.getProfile()

	const {
		execute: getCompany,
		data: companyData,
		isSuccess: isSuccessCompany,
	} = companyService.getCompanyMutation()

	useEffect(() => {
		if (isSuccess) getCompany({})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	useEffect(() => {
		if (isSuccessCompany && companyData) {
			if (companyData && !companyData?.white_label)
				storage.remove('company-color-primary')

			if (companyData?.color_primary && companyData?.white_label)
				storage.set('company-color-primary', companyData?.color_primary)

			dispatch(authCreators.setCompany(companyData))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyData, isSuccessCompany])

	useEffect(() => {
		document.documentElement.classList.toggle('dark', dark)
		storage.set('dark', dark)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dark])

	if (isError) navigate('/sair', { state: { redirect: location.pathname } })

	if (
		loadingProfile ||
		(auth.signed && !auth.user && !blockedRoutes.includes(location.pathname))
	) {
		return <Loader />
	}

	return (
		<>
			{auth.signed && auth.user && (
				<AmplitudeScript userId={auth.user?.id} company={auth.user?.company} />
			)}

			<Routes location={location} key={location.pathname}>
				{routes.map((route) => {
					const Layout = layouts[route?.layout || 'auth']

					if (route.isPrivate) {
						return (
							<Route
								key={route.path}
								{...route}
								element={
									<PrivateRoute roles={route.roles}>
										<Layout>
											<route.element />
										</Layout>
									</PrivateRoute>
								}
							/>
						)
					}

					return (
						<Route
							key={route.path}
							{...route}
							element={
								<Layout>
									<route.element />
								</Layout>
							}
						/>
					)
				})}
				<Route path='/sair' element={<LogoutRouter />} />
				<Route path='*' element={<Navigate to='/' />} />
			</Routes>
		</>
	)
}
